export const MAX_RATING_FEEDBACK_COMMENT_LENGTH = 1000;
export const MAX_RATING_FEEDBACK_REASONS = 6;

const FEEDBACK_OPTIONS_BY_CONTENT_TYPE = Object.freeze({
  question: Object.freeze([
    { value: "question-unclear", label: "Question unclear or ambiguous", group: "Question" },
    { value: "question-typo", label: "Question has a typo/grammar issue", group: "Question" },
    { value: "question-wrong-level", label: "Question tests the wrong level of detail", group: "Question" },
    { value: "correct-answer-wrong", label: "Correct answer seems wrong", group: "Answer options" },
    { value: "multiple-correct-answers", label: "More than one answer could be correct", group: "Answer options" },
    { value: "distractors-too-easy", label: "Distractors are too easy / implausible", group: "Answer options" },
    { value: "answer-options-poorly-worded", label: "Answer options are poorly worded", group: "Answer options" },
    { value: "explanation-incomplete", label: "Article doesn't justify why other options are wrong", group: "Answer options" },
    { value: "clinical-knowledge-incorrect", label: "Clinical/anatomy knowledge incorrect", group: "Article" },
    { value: "outdated-guidance", label: "Outdated guidance or terminology", group: "Article" },
    { value: "image-missing", label: "Image/diagram missing or needed", group: "Article" },
  ]),
  explanation: Object.freeze([
    { value: "explanation-unclear", label: "Article unclear" },
    { value: "supporting-images-poor", label: "Supporting images poor" },
    { value: "additional-info-poor", label: "Additional info poor" },
    { value: "clinical-knowledge-poor", label: "Clinical knowledge poor" },
  ]),
  "interview-station": Object.freeze([
    { value: "scenario-quality-poor", label: "Scenario quality poor" },
    { value: "model-answers-poor", label: "Model answers poor" },
    { value: "mark-scheme-poor", label: "Mark scheme poor" },
    { value: "clinical-knowledge-poor", label: "Clinical knowledge poor" },
  ]),
  "video-station": Object.freeze([
    { value: "video-quality-poor", label: "Video quality poor" },
    { value: "model-answers-poor", label: "Model answers poor" },
    { value: "additional-info-poor", label: "Additional info poor" },
    { value: "clinical-knowledge-poor", label: "Clinical knowledge poor" },
  ]),
  flashcard: Object.freeze([
    { value: "flashcard-prompt-poor", label: "Flashcard prompt poor" },
    { value: "flashcard-answer-poor", label: "Flashcard answer poor" },
    { value: "additional-info-poor", label: "Additional info poor" },
    { value: "clinical-knowledge-poor", label: "Clinical knowledge poor" },
  ]),
});

const DEFAULT_FEEDBACK_OPTIONS = FEEDBACK_OPTIONS_BY_CONTENT_TYPE.question;

export const getContentRatingFeedbackOptions = (contentType) =>
  [...(FEEDBACK_OPTIONS_BY_CONTENT_TYPE[contentType] || DEFAULT_FEEDBACK_OPTIONS)];

export const normalizeContentRatingFeedbackComment = (comment) =>
  String(comment || "")
    .trim()
    .slice(0, MAX_RATING_FEEDBACK_COMMENT_LENGTH);

export const normalizeContentRatingFeedbackSubmission = ({
  contentType,
  reasons = [],
  comment = "",
} = {}) => {
  const options = getContentRatingFeedbackOptions(contentType);
  const optionByValue = new Map(options.map((option) => [option.value, option]));
  const seen = new Set();
  const reasonKeys = [];
  const reasonLabels = [];

  (Array.isArray(reasons) ? reasons : []).forEach((reason) => {
    if (reasonKeys.length >= MAX_RATING_FEEDBACK_REASONS) return;
    const key = typeof reason === "string" ? reason.trim() : "";
    if (!key || seen.has(key) || !optionByValue.has(key)) return;
    seen.add(key);
    reasonKeys.push(key);
    reasonLabels.push(optionByValue.get(key).label);
  });

  return {
    reasonKeys,
    reasonLabels,
    comment: normalizeContentRatingFeedbackComment(comment),
  };
};

const coerceFeedbackCount = (value) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) {
    return Math.round(numeric);
  }
  return 0;
};

export const getRatingFeedbackCounts = (userRating = {}) => ({
  total: coerceFeedbackCount(userRating?.feedbackCount),
  currentVersion: coerceFeedbackCount(userRating?.currentVersionFeedbackCount),
});

export const formatRatingFeedbackCountLabel = (count) => {
  const safeCount = coerceFeedbackCount(count);
  return `${safeCount} feedback`;
};

export const formatRatingFeedbackActivatorLabel = (count) => {
  const safeCount = coerceFeedbackCount(count);
  if (safeCount === 0) return "View feedback reasons";
  return `View ${safeCount} feedback reason${safeCount === 1 ? "" : "s"}`;
};
